/**
 * Tresorerie : ce qui entre et sort reellement de chaque compte.
 *
 * Ici on raisonne en **flux**, jamais en charge reelle. La facture de dentiste
 * compte pour 400.00 le jour ou elle est payee, et chaque remboursement recu
 * est un credit sur le compte qui l'encaisse.
 */

import { somme } from '../domain/argent'
import { dernierJourDuMois, moisEntre } from '../domain/temps'
import type { Cents, Compte, DayKey, Id, MoisKey } from '../domain/types'
import type { Index } from './donnees'

export interface Mouvement {
  date: DayKey
  compteId: Id
  /** Signe : negatif pour une sortie. */
  montant: Cents
  libelle: string
  source: 'operation' | 'reglement'
  refId: Id
  /** Operation d'origine, y compris pour un reglement (la facture remboursee). */
  operationId: Id
}

export function mouvements(ix: Index): Mouvement[] {
  const out: Mouvement[] = []
  for (const op of ix.d.operations) {
    out.push({
      date: op.date,
      compteId: op.compteId,
      montant: op.sens === 'depense' ? -op.montant : op.montant,
      libelle: op.libelle,
      source: 'operation',
      refId: op.id,
      operationId: op.id,
    })
  }
  const opDePart = new Map(ix.d.parts.map((p) => [p.id, p.operationId]))
  for (const r of ix.d.reglements) {
    const opId = opDePart.get(r.partId) ?? ''
    const op = ix.operationParId.get(opId)
    const tiers = ix.d.parts.find((p) => p.id === r.partId)
    const nomTiers = tiers ? (ix.tiersParId.get(tiers.tiersId)?.nom ?? '') : ''
    out.push({
      date: r.date,
      compteId: r.compteId,
      montant: r.montant,
      libelle: `Remboursement ${nomTiers}${op ? ` — ${op.libelle}` : ''}`.trim(),
      source: 'reglement',
      refId: r.id,
      operationId: opId,
    })
  }
  return out.sort((a, b) => a.date.localeCompare(b.date))
}

/**
 * Solde d'un compte a la fin du jour `auJour`.
 *
 * Le solde initial est celui **au debut** de `soldeInitialLe` : les operations
 * de ce jour-la comptent. C'est le choix qui colle a l'usage — on cree le
 * compte avec le solde lu le matin, puis on saisit les depenses du jour.
 */
export function soldeAu(compte: Compte, mvts: readonly Mouvement[], auJour: DayKey): Cents {
  return (
    compte.soldeInitial +
    somme(
      mvts
        .filter((m) => m.compteId === compte.id && m.date >= compte.soldeInitialLe && m.date <= auJour)
        .map((m) => m.montant),
    )
  )
}

export interface PointSolde {
  mois: MoisKey
  solde: Cents
}

/** Solde de fin de chaque mois de la plage. `compteId` nul : tous comptes. */
export function courbeSoldes(
  ix: Index,
  mvts: readonly Mouvement[],
  du: MoisKey,
  au: MoisKey,
  compteId: Id | null,
): PointSolde[] {
  const comptes = ix.d.comptes.filter((c) => (compteId ? c.id === compteId : !c.archive))
  return moisEntre(du, au).map((mois) => {
    const fin = dernierJourDuMois(mois)
    return { mois, solde: somme(comptes.map((c) => soldeAu(c, mvts, fin))) }
  })
}
